import {
  Box,
  Button,
  DataList,
  Dialog,
  Icon,
  Input,
  Select,
  SelectItem,
  Skeleton,
  Text,
  createListCollection,
} from '@chakra-ui/react';
import { useForm } from '@tanstack/react-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPlus } from 'react-icons/fa';

import { type ApiError, CompaniesService, type CompanyCreate, KeycloakGroup } from '@/client';
import useCustomToast from '@/hooks/use-custom-toast';
import { UserRoleEnum, isRoleGreaterOrEqual, useUserRole } from '@/providers/user-role-provider';
import { handleError } from '@/utils';

function getCompaniesGroupsQueryOptions() {
  return {
    queryKey: ['companiesGroups'],
    queryFn: async () => CompaniesService.readCompaniesNotAlreadyJoined(),
  };
}

export const AddCompanyDialog = () => {
  const [open, setOpen] = useState(false);
  const { userRole } = useUserRole();

  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const showToast = useCustomToast();

  const { data: companiesSlugs, isLoading } = useQuery({
    ...getCompaniesGroupsQueryOptions(),
  });

  const form = useForm({
    defaultValues: {
      name: '',
      address: 'X',
      slug: '',
      is_template: false,
    },
    onSubmit: (values) => {
      mutation.mutate(values.value);
    },
  });

  const slugCollection = createListCollection({
    items:
      (companiesSlugs || []).map((company: KeycloakGroup) => ({
        id: company.id,
        name: company.name ?? '',
      })) || [],
    itemToString: (item: KeycloakGroup) => item.name,
    itemToValue: (item: KeycloakGroup) => item.name,
  });

  const mutation = useMutation({
    mutationFn: (data: CompanyCreate) =>
      CompaniesService.createCompany({
        requestBody: data,
      }),
    onSuccess: () => {
      showToast('Success!', 'Company created successfully.', 'success');
      setOpen(false);
    },
    onError: (err: ApiError) => {
      handleError(err, showToast);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['companies'] });
    },
  });

  if (!isRoleGreaterOrEqual(userRole, UserRoleEnum.CONSULTANT)) return null;

  return (
    <Dialog.Root lazyMount open={open} onOpenChange={(e) => setOpen(e.open)} placement="center">
      <Dialog.Trigger asChild>
        <Button gap={1} fontSize={{ base: 'sm', md: 'inherit' }}>
          <Icon as={FaPlus} /> {t('company.drawer.add.title')}
        </Button>
      </Dialog.Trigger>
      <Dialog.Backdrop />

      <Dialog.Positioner>
        <Dialog.Content
          as="form"
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          <Dialog.CloseTrigger />
          <Dialog.Header>
            <Dialog.Title>{t('company.drawer.add.title')}</Dialog.Title>
          </Dialog.Header>
          <Dialog.Body>
            <DataList.Root>
              <DataList.Item w="100%">
                <DataList.ItemLabel>{t('company.columnsHeader.name')}</DataList.ItemLabel>
                <DataList.ItemValue>
                  <form.Field name="name">
                    {(field) => (
                      <Box w="100%">
                        <Input
                          id="name"
                          type="text"
                          value={field.state.value}
                          onChange={(e) => field.handleChange(e.target.value)}
                        />
                        {field.state.meta.errors?.length > 0 && (
                          <Text color="red.500" fontSize="sm" mt={2}>
                            {field.state.meta.errors[0]}
                          </Text>
                        )}
                      </Box>
                    )}
                  </form.Field>
                </DataList.ItemValue>
              </DataList.Item>

              {isLoading ? (
                <Skeleton height="8" />
              ) : (
                <DataList.Item w={'full'}>
                  <DataList.ItemLabel>{t('company.columnsHeader.client.id')}</DataList.ItemLabel>
                  <DataList.ItemValue>
                    <form.Field name="slug">
                      {(field) => (
                        <Select.Root
                          collection={slugCollection}
                          defaultValue={[field.state.value]}
                          size="sm"
                          onValueChange={(e) => {
                            field.handleChange(e.value[0]);
                          }}
                        >
                          <Select.Control>
                            <Select.Trigger>
                              <Select.ValueText placeholder={t('company.select.client.id')} />
                            </Select.Trigger>
                            <Select.IndicatorGroup>
                              <Select.Indicator />
                            </Select.IndicatorGroup>
                          </Select.Control>
                          <Select.Positioner>
                            <Select.Content>
                              {slugCollection?.items.map((slug) => (
                                <SelectItem item={slug} key={slug.id}>
                                  <Text>{slug.name}</Text>
                                </SelectItem>
                              ))}
                            </Select.Content>
                          </Select.Positioner>
                        </Select.Root>
                      )}
                    </form.Field>
                  </DataList.ItemValue>
                </DataList.Item>
              )}
            </DataList.Root>
          </Dialog.Body>
          <Dialog.Footer>
            <Button onClick={() => setOpen(false)} variant="outline">
              {t('ui.cancel')}
            </Button>
            <Button type="submit">{t('ui.save')}</Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
};
